import { scopedApi, type ApiScope } from "@/features/utils/api-scope";
import { api } from "@/features/utils/fetch-server";
import type {
  AssignableDevice,
  DeviceBase,
  DeviceBaseHistoryEntry,
  DeviceBaseInput,
  DeviceCall,
  Device,
  DeviceInput,
  DeviceWithCredentials,
} from "../types";

/**
 * Where this caller's handset routes live.
 *
 * Every method takes the scope rather than reading it, so the API layer stays a
 * pure function of its arguments; the hooks are the ones that know which
 * back-office they are rendering (`useApiScope`).
 */
const base = (scope: ApiScope, clientId: string) =>
  scopedApi(scope, "sessions", clientId);

export class DevicesAPI {
  static getDevices = async (
    scope: ApiScope,
    clientId: string,
    filters?: { connector_id?: string },
  ): Promise<Device[]> => {
    const params = new URLSearchParams();
    if (filters?.connector_id)
      params.append("connector_id", filters.connector_id);

    const queryString = params.toString();
    return api.get<Device[]>(
      `${base(scope, clientId)}/devices${queryString ? `?${queryString}` : ""}`,
    );
  };

  /**
   * The handsets a member may point their own profile at — readable by every
   * member, unlike the provisioning list above, and carrying only what a picker
   * needs. Tenant-only: it answers a question about oneself, which is not a
   * question platform staff ever ask.
   */
  static getAssignableDevices = async (
    clientId: string,
  ): Promise<AssignableDevice[]> =>
    api.get<AssignableDevice[]>(
      `/api/sessions/v1/${clientId}/devices/assignable`,
    );

  static getDevice = async (
    scope: ApiScope,
    clientId: string,
    deviceId: string,
  ): Promise<Device> =>
    api.get<Device>(`${base(scope, clientId)}/devices/${deviceId}`);

  /** The response carries the plaintext SIP password — show it, do not refetch it. */
  static createDevice = async (
    scope: ApiScope,
    clientId: string,
    data: DeviceInput,
  ): Promise<DeviceWithCredentials> =>
    api.post<DeviceWithCredentials, DeviceInput>(
      `${base(scope, clientId)}/devices`,
      data,
    );

  static updateDevice = async (
    scope: ApiScope,
    clientId: string,
    deviceId: string,
    data: DeviceInput,
  ): Promise<Device> =>
    api.put<Device, DeviceInput>(
      `${base(scope, clientId)}/devices/${deviceId}`,
      data,
    );

  static deleteDevice = async (
    scope: ApiScope,
    clientId: string,
    deviceId: string,
  ): Promise<{ success: boolean }> =>
    api.delete<{ success: boolean }>(
      `${base(scope, clientId)}/devices/${deviceId}`,
    );

  /** Issues a new password; the handset is offline until it is reconfigured. */
  static regenerateCredentials = async (
    scope: ApiScope,
    clientId: string,
    deviceId: string,
  ): Promise<DeviceWithCredentials> =>
    api.post<DeviceWithCredentials, undefined>(
      `${base(scope, clientId)}/devices/${deviceId}/credentials`,
    );

  /** Recent attempts, newest first — refusals included. */
  static getDeviceCalls = async (
    scope: ApiScope,
    clientId: string,
    deviceId: string,
  ): Promise<DeviceCall[]> =>
    (
      await api.get<{ calls: DeviceCall[] }>(
        `${base(scope, clientId)}/devices/${deviceId}/calls`,
      )
    ).calls;

  static ringDevice = async (
    scope: ApiScope,
    clientId: string,
    deviceId: string,
  ): Promise<{ success: boolean }> =>
    api.post<{ success: boolean }, undefined>(
      `${base(scope, clientId)}/devices/${deviceId}/ring`,
    );

  /**
   * Every base of the tenant, each carrying the handsets that run on it — one
   * call for a screen that shows the two together.
   */
  static getBases = async (
    scope: ApiScope,
    clientId: string,
  ): Promise<DeviceBase[]> =>
    (await api.get<{ bases: DeviceBase[] }>(`${base(scope, clientId)}/bases`))
      .bases;

  static createBase = async (
    scope: ApiScope,
    clientId: string,
    data: DeviceBaseInput,
  ): Promise<DeviceBase> =>
    (
      await api.post<{ base: DeviceBase }, DeviceBaseInput>(
        `${base(scope, clientId)}/bases`,
        data,
      )
    ).base;

  static updateBase = async (
    scope: ApiScope,
    clientId: string,
    baseId: string,
    data: DeviceBaseInput,
  ): Promise<DeviceBase> =>
    (
      await api.put<{ base: DeviceBase }, DeviceBaseInput>(
        `${base(scope, clientId)}/bases/${baseId}`,
        data,
      )
    ).base;

  /** The handsets on it are detached, not deleted — they are SIP accounts. */
  static deleteBase = async (
    scope: ApiScope,
    clientId: string,
    baseId: string,
  ): Promise<{ success: boolean }> =>
    api.delete<{ success: boolean }>(`${base(scope, clientId)}/bases/${baseId}`);

  /** SYSADMIN only — the server refuses this one to a tenant's own owner. */
  static getBaseHistory = async (
    scope: ApiScope,
    clientId: string,
    baseId: string,
  ): Promise<DeviceBaseHistoryEntry[]> =>
    (
      await api.get<{ history: DeviceBaseHistoryEntry[] }>(
        `${base(scope, clientId)}/bases/${baseId}/history`,
      )
    ).history;
}
